/**
 * Welcome `/welcome` — the one-time profile CLAIM step. Sign-in/registration
 * lands here (Login redirects on success); a profile that already carries
 * `claimedAt` immediately continues to the login return target, so returning
 * users never see the form. Claiming = a handle (prefilled from the bootstrap stub, validated with
 * the SAME shared rules the server enforces) + a REQUIRED avatar choice, sent
 * through the one trusted writer — the server stamps `claimedAt`.
 */
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HANDLE_MAX_LEN, canonicalHandle } from '@djgg/creations';
import { useMyProfile, useServices } from '../services/context.js';
import { AvatarPicker } from '../components/AvatarPicker.js';
import { Mascot } from '../components/Mascot.js';
import { handleError } from './Account.js';
import { gameReturnFromSearch } from '../auth/gameReturn.js';

export function Welcome() {
  const services = useServices();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = gameReturnFromSearch(location.search);
  const { loading, profile } = useMyProfile();

  const [handle, setHandle] = useState('');
  const [avatarKey, setAvatarKey] = useState('');
  const [seeded, setSeeded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Prefill the handle ONCE, when the bootstrap stub first appears in the
  // directory (a missing stub row just starts the form empty).
  useEffect(() => {
    if (seeded || !profile?.handle) return;
    setHandle(profile.handle);
    setSeeded(true);
  }, [seeded, profile]);

  if (loading) {
    return <div className="empty">Loading your profile…</div>;
  }
  // Already claimed — nothing to do here.
  if (profile?.claimedAt) {
    return <Navigate to={returnTo} replace />;
  }

  const validation = handleError(handle);
  const ready = !validation && avatarKey !== '';

  async function claim(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (busy || !ready) return;
    setBusy(true);
    setError('');
    try {
      await services.callable.updateMyProfile({
        handle: canonicalHandle(handle),
        avatarKey,
      });
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not claim your profile.');
      setBusy(false);
    }
  }

  return (
    <div className="container auth" data-testid="welcome-page">
      <Mascot context="login" size={200} />
      <div className="panel" style={{ marginTop: 14, maxWidth: 640 }}>
        <h1 className="panel__title">Welcome to the flock</h1>
        <p className="muted" style={{ marginTop: -6 }}>
          Claim your profile: pick a handle and an avatar. Both show up beside your games,
          comments and likes.
        </p>
        <form onSubmit={(e) => void claim(e)}>
          <div className="field">
            <label className="field__label" htmlFor="welcome-handle">
              Handle
            </label>
            <input
              id="welcome-handle"
              className="input"
              value={handle}
              maxLength={HANDLE_MAX_LEN}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="your-handle"
              autoFocus
              data-testid="welcome-handle"
            />
            {handle && validation && (
              <div className="error-text" data-testid="welcome-handle-error">
                {validation}
              </div>
            )}
          </div>
          <div className="field">
            <span className="field__label">Avatar (required)</span>
            <AvatarPicker value={avatarKey} onChange={setAvatarKey} disabled={busy} />
          </div>
          {error && (
            <div className="error-text" data-testid="welcome-error">
              {error}
            </div>
          )}
          <button
            className="btn btn--primary"
            type="submit"
            disabled={busy || !ready}
            style={{ width: '100%' }}
            data-testid="welcome-claim"
          >
            {busy ? 'Claiming…' : 'Claim profile'}
          </button>
        </form>
      </div>
    </div>
  );
}
